import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { theme } from '../../constants/theme';

const FieldWrapper = styled.div`
  margin-bottom: ${theme.spacing.md};
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.normal};
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.25rem;
  line-height: ${theme.typography.lineHeight.normal};

  &.required::after {
    content: ' *';
    color: ${theme.colors.error.main};
    font-size: ${theme.typography.fontSize.md};
  }

  &.disabled {
    color: ${theme.colors.text.disabled};
  }
`;

const ErrorText = styled.span`
  display: block;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.error.main};
  margin-top: 0.25rem;
  line-height: ${theme.typography.lineHeight.tight};
`;

const HelperText = styled.span`
  display: block;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
  line-height: ${theme.typography.lineHeight.tight};
`;

/**
 * CustomFormField Component
 * 
 * A wrapper component that combines label, input component, and error/helper text
 * into a consistent form field layout.
 * 
 * @component
 * @example
 * ```jsx
 * <CustomFormField
 *   label="Email Address"
 *   required
 *   error="Please enter a valid email"
 * >
 *   <CustomInput
 *     type="email"
 *     value={email}
 *     onChange={(e) => setEmail(e.target.value)}
 *   />
 * </CustomFormField>
 * ```
 */
const CustomFormField = ({
  label = '',
  required = false,
  error = '',
  helperText = '',
  disabled = false,
  children,
  className = '',
  htmlFor,
}) => {
  const labelClassName = [
    required && 'required',
    disabled && 'disabled',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FieldWrapper className={className}>
      {label && (
        <Label htmlFor={htmlFor} className={labelClassName}>
          {label}
        </Label>
      )}
      {children}
      {error && <ErrorText>{error}</ErrorText>}
      {!error && helperText && <HelperText>{helperText}</HelperText>}
    </FieldWrapper>
  );
};

CustomFormField.propTypes = {
  /** Field label */
  label: PropTypes.string,
  
  /** Mark field as required */
  required: PropTypes.bool,
  
  /** Error message to display */
  error: PropTypes.string,
  
  /** Helper text to display */
  helperText: PropTypes.string,
  
  /** Disabled state (affects label styling) */
  disabled: PropTypes.bool,
  
  /** Input component */
  children: PropTypes.node.isRequired,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** ID of the input element (for label htmlFor) */
  htmlFor: PropTypes.string,
};

export default CustomFormField;
